import React, { useCallback, useMemo, useState } from 'react';
import { AdminDevTh, btnSecondary, tdClass } from './adminUi';

export type AdminColumnDef<T> = {
  id: string;
  label: string;
  dbField?: string;
  /** По умолчанию true. Столбец «Действия» — false. */
  hideable?: boolean;
  defaultVisible?: boolean;
  headerClassName?: string;
  cellClassName?: string;
  render: (row: T) => React.ReactNode;
};

const STORAGE_PREFIX = 'dc_admin_hidden_cols_v1:';

function readHiddenIds(storageKey: string, columns: AdminColumnDef<unknown>[]): Set<string> {
  const defaults = new Set(
    columns
      .filter((c) => c.hideable !== false && c.defaultVisible === false)
      .map((c) => c.id),
  );
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return defaults;
    const valid = new Set(columns.map((c) => c.id));
    return new Set(parsed.filter((id): id is string => typeof id === 'string' && valid.has(id)));
  } catch {
    return defaults;
  }
}

function writeHiddenIds(storageKey: string, hidden: Set<string>) {
  localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, JSON.stringify(Array.from(hidden)));
}

export function useAdminColumnVisibility<T>(storageKey: string, columns: AdminColumnDef<T>[]) {
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() =>
    readHiddenIds(storageKey, columns as AdminColumnDef<unknown>[]),
  );

  const visibleColumns = useMemo(
    () => columns.filter((c) => c.hideable === false || !hiddenIds.has(c.id)),
    [columns, hiddenIds],
  );

  const toggleColumn = useCallback(
    (id: string) => {
      const col = columns.find((c) => c.id === id);
      if (!col || col.hideable === false) return;
      setHiddenIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        writeHiddenIds(storageKey, next);
        return next;
      });
    },
    [columns, storageKey],
  );

  const showAllColumns = useCallback(() => {
    setHiddenIds(new Set());
    writeHiddenIds(storageKey, new Set());
  }, [storageKey]);

  const hideableColumns = useMemo(
    () => columns.filter((c) => c.hideable !== false),
    [columns],
  );

  return {
    visibleColumns,
    hideableColumns,
    hiddenIds,
    toggleColumn,
    showAllColumns,
  };
}

export function AdminColumnPicker({
  hideableColumns,
  hiddenIds,
  onToggle,
  onShowAll,
}: {
  hideableColumns: { id: string; label: string }[];
  hiddenIds: Set<string>;
  onToggle: (id: string) => void;
  onShowAll: () => void;
}) {
  if (hideableColumns.length === 0) return null;

  const hiddenCount = hideableColumns.filter((c) => hiddenIds.has(c.id)).length;

  return (
    <details className="dc-column-picker relative">
      <summary className={`${btnSecondary} dc-column-picker-trigger whitespace-nowrap list-none cursor-pointer`}>
        <span className="inline-flex items-center gap-1.5">
          <i className="fas fa-table-columns text-xs" aria-hidden="true" />
          Столбцы
          {hiddenCount > 0 && (
            <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
              −{hiddenCount}
            </span>
          )}
        </span>
      </summary>
      <div className="dc-column-picker-menu" role="menu">
        <p className="mb-2 text-xs font-medium text-slate-500">Отображаемые столбцы</p>
        <ul className="max-h-64 space-y-1 overflow-y-auto">
          {hideableColumns.map((col) => {
            const checked = !hiddenIds.has(col.id);
            return (
              <li key={col.id}>
                <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300"
                    checked={checked}
                    onChange={() => onToggle(col.id)}
                  />
                  {col.label}
                </label>
              </li>
            );
          })}
        </ul>
        <button
          type="button"
          className="mt-2 w-full text-left text-xs font-medium text-dc-primary hover:underline"
          onClick={onShowAll}
        >
          Показать все
        </button>
      </div>
    </details>
  );
}

export function AdminConfigurableTable<T>({
  columns,
  rows,
  rowKey,
  minWidth = 900,
}: {
  columns: AdminColumnDef<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  minWidth?: number;
}) {
  return (
    <table className="w-full text-sm" style={{ minWidth }}>
      <thead className="border-b border-slate-100 bg-slate-50/80">
        <tr>
          {columns.map((col) => (
            <AdminDevTh
              key={col.id}
              label={col.label}
              dbField={col.dbField}
              className={col.headerClassName}
            />
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row) => (
          <tr key={rowKey(row)} className="hover:bg-slate-50/50">
            {columns.map((col) => (
              <td key={col.id} className={col.cellClassName || tdClass}>
                {col.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
